import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, type Profile } from "@/hooks/useAuth";
import { api } from "@/lib/api";
import { plans, planText, type Plan } from "@/lib/plans";
import { currentLanguage, LOCALES } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo from "@/pages/home/components/Logo";
import OrderHistory from "@/pages/orders/components/OrderHistory";
import CustomPlanCard from "@/pages/pricing/components/CustomPlanCard";
import Footer from "@/pages/home/components/Footer";

export default function Profile() {
  const { t } = useTranslation();
  const { user, profile, loading, ready, signOut, setProfile } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCheckout = (tier: Plan) => {
    navigate(`/checkout?plan=${encodeURIComponent(tier.name)}`);
  };

  useEffect(() => {
    if (ready && !user) navigate("/auth", { replace: true });
  }, [ready, user, navigate]);

  useEffect(() => {
    if (profile) setName(profile.full_name ?? "");
  }, [profile]);

  const initials = ((profile?.full_name || user?.email || "?") as string)
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const { profile: updated } = await api.patch<{ profile: Profile }>("/auth/profile", {
        fullName: name.trim() || null,
      });
      setProfile(updated);
      setMessage(t("profile.details.updated"));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("profile.details.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="w-10 h-10 rounded-full loader-ring"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-lock-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading font-bold text-xl text-foreground-950">{t("profile.signInRequired.title")}</h1>
          <p className="mt-2 text-sm text-foreground-600">
            {t("profile.signInRequired.body")}
          </p>
          <Link
            to="/auth"
            className="mt-6 inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.02] cursor-pointer"
          >
            {t("common.nav.signIn")}
          </Link>
        </div>
      </div>
    );
  }

  const planId = profile?.plan || "free";
  const planLabel = t(`profile.planNames.${planId.toLowerCase()}`, {
    defaultValue: planId.charAt(0).toUpperCase() + planId.slice(1),
  });

  return (
    <div className="min-h-screen bg-background-50">
      <header className="border-b border-background-200/70 bg-background-50/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto h-16 px-4 md:px-6 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <Link
              to="/generate"
              className="whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.03] cursor-pointer"
            >
              <i className="ri-magic-line mr-1.5"></i>
              {t("common.nav.generate")}
            </Link>
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors"
            >
              {t("common.actions.backToHome")}
            </Link>
            <button
              type="button"
              onClick={handleSignOut}
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors cursor-pointer"
            >
              {t("common.nav.signOut")}
            </button>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="font-heading font-bold text-2xl text-background-50">{initials}</span>
              </div>
              <h1 className="mt-4 font-heading font-bold text-lg text-foreground-950">
                {profile?.full_name || t("profile.yourName")}
              </h1>
              <p className="mt-1 text-sm text-foreground-500">{user.email}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-accent-100 text-accent-900 px-3 py-1 text-xs font-medium">
                <i className="ri-vip-crown-line"></i>
                {t("profile.planBadge", { plan: planLabel })}
              </span>
              <div className="mt-5 flex flex-col gap-2">
                <Link
                  to="/pricing"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-3 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.03] cursor-pointer"
                >
                  <i className="ri-arrow-up-circle-line text-lg"></i>
                  {t("profile.upgradePlan")}
                </Link>
                <Link
                  to="/generate"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-md border border-background-300/60 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-200/50 transition-colors cursor-pointer"
                >
                  <i className="ri-magic-line text-lg"></i>
                  {t("profile.startGenerating")}
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="bg-background-100 border border-background-200/70 rounded-lg p-6">
              <h2 className="font-heading font-bold text-base text-foreground-950">{t("profile.details.title")}</h2>
              <form onSubmit={handleSave} className="mt-5 space-y-4">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    {t("profile.details.fullName")}
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("profile.details.fullNamePlaceholder")}
                    className="w-full rounded-md border border-background-300/60 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                {message && (
                  <div className="rounded-md bg-accent-100 text-accent-900 px-4 py-3 text-sm">{message}</div>
                )}
                {error && <div className="rounded-md bg-red-100 text-red-700 px-4 py-3 text-sm">{error}</div>}

                <button
                  type="submit"
                  disabled={saving}
                  className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer"
                >
                  {saving ? t("profile.details.saving") : t("profile.details.save")}
                </button>
              </form>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 relative">
                <div className="text-sm text-foreground-500">{t("profile.stats.credits")}</div>
                <div className="mt-2 font-heading font-bold text-3xl text-foreground-950">{profile?.credits ?? 0}</div>
                <Link
                  to="/pricing"
                  className="mt-4 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-add-circle-line text-lg"></i>
                  {t("profile.stats.buyMore")}
                </Link>
              </div>
              <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
                <div className="text-sm text-foreground-500">{t("profile.stats.memberSince")}</div>
                <div className="mt-2 font-heading font-bold text-lg text-foreground-950">
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString(LOCALES[currentLanguage()]) : "—"}
                </div>
              </div>
            </div>

            <OrderHistory />
          </div>
        </div>

        {/* Plans */}
        <div className="mt-14">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
              {t("profile.plans.eyebrow")}
            </span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-foreground-950">
              {t("profile.plans.title")}
            </h2>
            <p className="mt-3 text-foreground-600">
              {t("profile.plans.subtitle")}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
            {plans.map((tier) => {
              const text = planText(t, tier);
              return (
                <div
                  key={tier.name}
                  className={`relative flex flex-col rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 ${
                    tier.highlighted
                      ? "bg-gradient-to-b from-primary-600 to-accent-600 text-background-50 shadow-xl"
                      : "bg-background-50 border border-background-200/70 text-foreground-950"
                  }`}
                >
                  {tier.highlighted && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-background-50 text-primary-600 text-xs font-semibold px-4 py-1 shadow-sm">
                      {t("profile.plans.mostPopular")}
                    </span>
                  )}

                  <h3 className="text-lg font-bold">{tier.name}</h3>
                  <div className="mt-4 flex items-end gap-1">
                    <span className="text-4xl md:text-5xl font-bold tracking-tight">{text.price}</span>
                    <span className={`text-sm mb-2 ${tier.highlighted ? "text-background-50/80" : "text-foreground-500"}`}>
                      {text.period}
                    </span>
                  </div>
                  <p className={`mt-2 text-sm ${tier.highlighted ? "text-background-50/80" : "text-foreground-600"}`}>
                    {text.tagline}
                  </p>
                  <div className={`mt-2 inline-flex items-center gap-1.5 text-sm font-medium ${tier.highlighted ? "text-accent-200" : "text-primary-600"}`}>
                    <i className="ri-coins-line"></i>
                    {text.credits}
                  </div>

                  <ul className="mt-6 flex-1 space-y-3">
                    {text.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm">
                        <i
                          className={`ri-checkbox-circle-fill text-lg mt-0.5 ${
                            tier.highlighted ? "text-accent-200" : "text-primary-500"
                          }`}
                        ></i>
                        <span className={tier.highlighted ? "text-background-50/90" : "text-foreground-700"}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={() => handleCheckout(tier)}
                    className={`mt-8 w-full whitespace-nowrap rounded-full py-3.5 text-sm font-semibold transition-transform hover:scale-[1.02] cursor-pointer ${
                      tier.highlighted
                        ? "bg-background-50 text-primary-700"
                        : "bg-gradient-to-r from-primary-500 to-accent-500 text-background-50"
                    }`}
                  >
                    {text.cta}
                  </button>
                </div>
              );
            })}
          </div>

          <CustomPlanCard />
        </div>
      </main>

      <Footer />
    </div>
  );
}